import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";

import {
  createPlaceAutocompleteSessionToken,
  fetchAddressPredictions,
  fetchPlaceDetails,
  MIN_ADDRESS_AUTOCOMPLETE_CHARS,
} from "./phlebotomyAddressAutocompleteUtils";
import type {
  PlacePredictionOption,
  PlacePredictionsById,
} from "./phlebotomyAddressAutocompleteUtils";

export interface PhlebotomyStructuredAddress {
  placeId: string;
  formattedAddress: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  lat?: number;
  lng?: number;
}

interface PhlebotomyAddressAutocompleteProps {
  label: string;
  inputValue: string;
  value: PhlebotomyStructuredAddress | null;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  onInputValueChange: (value: string) => void;
  onChange: (value: PhlebotomyStructuredAddress | null) => void;
  onBusyChange?: (busy: boolean) => void;
}

const getAddressComponent = (
  components: google.maps.places.AddressComponent[] | undefined,
  type: string,
) => components?.find((component) => component.types.includes(type));

const parsePlaceAddress = (
  placeId: string,
  place: google.maps.places.Place,
): PhlebotomyStructuredAddress => {
  const components = place.addressComponents;
  const streetNumber = getAddressComponent(
    components,
    "street_number",
  )?.longText;
  const route = getAddressComponent(components, "route")?.longText;
  const premise = getAddressComponent(components, "premise")?.longText;
  const subpremise = getAddressComponent(components, "subpremise")?.longText;
  const city =
    getAddressComponent(components, "locality")?.longText ||
    getAddressComponent(components, "postal_town")?.longText ||
    getAddressComponent(components, "sublocality_level_1")?.longText ||
    "";
  const state =
    getAddressComponent(components, "administrative_area_level_1")?.shortText ||
    "";
  const postalCode =
    getAddressComponent(components, "postal_code")?.longText || "";
  const formattedAddress = place.formattedAddress || "";
  const [formattedFirstLine] = formattedAddress.split(",");
  const addressLine1 =
    [streetNumber, route].filter(Boolean).join(" ") ||
    premise ||
    formattedFirstLine?.trim() ||
    "";
  const { location } = place;

  if (!location || !addressLine1 || !city || !state || !postalCode) {
    throw new Error("Selected place is missing required address fields");
  }

  return {
    placeId,
    formattedAddress,
    addressLine1,
    addressLine2: subpremise || "",
    city,
    state,
    postalCode: postalCode.slice(0, 5),
    lat: location.lat(),
    lng: location.lng(),
  };
};

const PhlebotomyAddressAutocomplete: React.FC<
  PhlebotomyAddressAutocompleteProps
> = ({
  label,
  inputValue,
  value,
  disabled = false,
  error = false,
  helperText,
  onInputValueChange,
  onChange,
  onBusyChange,
}) => {
  const { t } = useTranslation();
  const places = useMapsLibrary("places");
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const predictionsByPlaceIdRef = useRef<PlacePredictionsById>(new Map());
  const [options, setOptions] = useState<PlacePredictionOption[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [resolvingPlace, setResolvingPlace] = useState(false);
  const [hasFetchedOptions, setHasFetchedOptions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const hasQuery = inputValue.trim().length >= MIN_ADDRESS_AUTOCOMPLETE_CHARS;
  const selectedOption = useMemo<PlacePredictionOption | null>(
    () =>
      value
        ? {
            placeId: value.placeId,
            primaryText: value.addressLine1,
            secondaryText: [value.city, value.state, value.postalCode]
              .filter(Boolean)
              .join(", "),
            description: value.formattedAddress,
          }
        : null,
    [value],
  );

  useEffect(() => {
    if (!places) {
      sessionTokenRef.current = null;
      predictionsByPlaceIdRef.current.clear();
      return;
    }
    sessionTokenRef.current = createPlaceAutocompleteSessionToken(places);
  }, [places]);

  useEffect(() => {
    if (!places || value || !hasQuery) {
      setOptions([]);
      setLoadingPredictions(false);
      setHasFetchedOptions(false);
      if (!value) predictionsByPlaceIdRef.current.clear();
      return;
    }

    let cancelled = false;
    setLoadingPredictions(true);
    setHasFetchedOptions(false);
    const sessionToken =
      sessionTokenRef.current ?? createPlaceAutocompleteSessionToken(places);
    sessionTokenRef.current = sessionToken;

    const timeoutId = window.setTimeout(async () => {
      try {
        const { options: nextOptions, predictionsByPlaceId } =
          await fetchAddressPredictions(places, inputValue, sessionToken);
        if (cancelled) return;

        predictionsByPlaceIdRef.current = predictionsByPlaceId;
        setOptions(nextOptions);
        setHasFetchedOptions(true);
      } catch {
        if (cancelled) return;
        predictionsByPlaceIdRef.current.clear();
        setOptions([]);
        setHasFetchedOptions(true);
      } finally {
        if (!cancelled) setLoadingPredictions(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [hasQuery, inputValue, places, value]);

  useEffect(() => {
    if (value || !hasQuery) {
      setIsOpen(false);
      return;
    }
    if (loadingPredictions || hasFetchedOptions) setIsOpen(true);
  }, [hasFetchedOptions, hasQuery, loadingPredictions, value]);

  const setResolving = (next: boolean) => {
    setResolvingPlace(next);
    onBusyChange?.(next);
  };

  const handleSelection = async (option: PlacePredictionOption) => {
    if (!places) {
      setLookupError(t("phlebotomySearch.errors.addressNotFound"));
      return;
    }
    setLookupError(null);
    setResolving(true);
    try {
      const place = await fetchPlaceDetails(
        places,
        predictionsByPlaceIdRef.current,
        option.placeId,
      );
      const address = parsePlaceAddress(option.placeId, place);
      onInputValueChange(address.formattedAddress);
      onChange(address);
      setOptions([]);
      setIsOpen(false);
      sessionTokenRef.current = createPlaceAutocompleteSessionToken(places);
      predictionsByPlaceIdRef.current.clear();
    } catch {
      onChange(null);
      setLookupError(t("phlebotomySearch.errors.addressNotFound"));
    } finally {
      setResolving(false);
    }
  };

  const busy = loadingPredictions || resolvingPlace;

  return (
    <Autocomplete
      fullWidth
      options={options}
      filterOptions={(nextOptions) => nextOptions}
      loading={loadingPredictions}
      disabled={disabled || resolvingPlace}
      value={selectedOption}
      inputValue={inputValue}
      open={isOpen}
      openOnFocus={false}
      forcePopupIcon={false}
      noOptionsText={
        hasQuery && hasFetchedOptions
          ? t("phlebotomySearch.search.noAddressOptions")
          : null
      }
      onOpen={() => {
        if (hasQuery && !value) setIsOpen(true);
      }}
      onClose={() => setIsOpen(false)}
      getOptionLabel={(option) => option.description}
      isOptionEqualToValue={(option, selected) =>
        option.placeId === selected.placeId
      }
      onInputChange={(_event, nextValue, reason) => {
        if (reason !== "input" && reason !== "clear") return;
        onInputValueChange(nextValue);
        setLookupError(null);
        if (!value || nextValue !== value.formattedAddress) onChange(null);
      }}
      onChange={(_event, option) => {
        if (!option) {
          onChange(null);
          return;
        }
        handleSelection(option).catch(() => undefined);
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.placeId}>
          <Typography component="span" variant="body1">
            {option.primaryText}
            {option.secondaryText ? (
              <Typography
                component="span"
                variant="body1"
                color="text.secondary"
              >
                {` ${option.secondaryText}`}
              </Typography>
            ) : null}
          </Typography>
        </li>
      )}
      renderInput={(params) => {
        const { InputProps, inputProps, ...textFieldParams } = params;
        return (
          <TextField
            {...textFieldParams}
            label={label}
            error={error || Boolean(lookupError)}
            helperText={lookupError || helperText}
            InputProps={{
              ...InputProps,
              inputProps,
              endAdornment: (
                <>
                  {busy ? <CircularProgress color="inherit" size={18} /> : null}
                  {InputProps.endAdornment}
                </>
              ),
            }}
          />
        );
      }}
    />
  );
};

export default PhlebotomyAddressAutocomplete;
